// src/ClinicalDataCollector/components/output/PreventiveServicesReport.jsx
import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Divider,
  Box,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TodayIcon from '@mui/icons-material/Today';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { generatePreventiveServicesFromAI } from '../../../../utils/services/preventiveAiService';
import { buildHedisContextText } from '../../../../utils/clinicalNotesFormatter';

// Botón amarillo + modal de entradas
const PreventiveInfoButton = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title="Show data inputs for this section">
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          sx={{
            bgcolor: 'rgba(255, 193, 7, 0.2)',
            '&:hover': { bgcolor: 'rgba(255, 193, 7, 0.35)' },
          }}
        >
          <InfoOutlinedIcon color="warning" fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Data inputs – Preventive Services Report</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            This section summarizes USPSTF A/B preventive services that apply
            to the patient. In production, it is fed by:
          </Typography>
          <ul>
            <li>
              <Typography variant="body2">
                <strong>Preventive services / USPSTF engine</strong> that
                determines which screenings and interventions apply based on
                age, sex and risk factors.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Claims and procedure history</strong> for colonoscopy,
                mammography, Pap tests, DEXA scans, etc., including dates of
                completion.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Lab and screening results</strong> for items such as
                depression screening (PHQ-9), hepatitis C, HIV, and other
                recommended tests.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Patient demographics</strong> (age, sex, comorbidities
                or risk factors) to decide applicability.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Optionally, <strong>clinical notes</strong> where screenings
                are documented but not yet coded as procedures.
              </Typography>
            </li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} variant="contained" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// 🔹 Ahora el componente recibe el contexto real del paciente
const PreventiveServicesReport = ({
  demographics,
  annualWellnessNotes,
  followUpExamNotes,
  telehealthNotes,
  hospitalDischargeNotes,
  medications = [],
  labs = [],
  allergies = [],
}) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);

    try {
      // 👇 mismo builder que HEDIS: demographics + notas + meds + labs + alergias
      const preventiveContextText = buildHedisContextText({
        demographics,
        annualWellnessNotes,
        followUpExamNotes,
        telehealthNotes,
        hospitalDischargeNotes,
        medications,
        labs,
        allergies,
      });

      if (!preventiveContextText.trim()) {
        setError('No clinical data available for preventive-services evaluation.');
        setServices([]);
        return;
      }

      const aiServices = await generatePreventiveServicesFromAI({
        preventiveContextText,
      });

      setServices(Array.isArray(aiServices) ? aiServices : []);
    } catch (err) {
      console.error('[PreventiveServicesReport] Error generating services:', err);
      setError('Error generating preventive services with AI.');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
      <CardHeader
        title="USPTF Preventive Services Report"
        titleTypographyProps={{ variant: 'h5', align: 'center' }}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <PreventiveInfoButton />

            <Button
              variant="contained"
              size="small"
              onClick={handleGenerate}
              disabled={loading}
            >
              {loading ? 'Generating…' : 'Generate preventive services with AI'}
            </Button>

            <MedicalServicesIcon fontSize="large" color="primary" />
          </Stack>
        }
      />
      <Divider />
      <CardContent>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 3,
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Generating preventive services with AI...
            </Typography>
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && services.length === 0 && (
          <Typography variant="body2">
            No data available yet. Click &quot;Generate preventive services with AI&quot;.
          </Typography>
        )}

        {!loading && !error && services.length > 0 && (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">
                      Preventive Service
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="subtitle2">
                      USPSTF Grade
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="subtitle2">
                      Status
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      Last Performed
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      Next Recommended
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((item, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{item.name || item.service || '—'}</TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {item.grade || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {item.status === 'Closed' ? (
                        <CheckCircleIcon color="success" />
                      ) : item.status === 'NotApplicable' ? (
                        <Typography variant="caption" color="text.secondary">
                          N/A
                        </Typography>
                      ) : (
                        <WarningAmberIcon color="warning" />
                      )}
                    </TableCell>
                    <TableCell>
                      <TodayIcon
                        sx={{ mr: 0.5, verticalAlign: 'middle' }}
                      />
                      {item.lastPerformed || item.lastDate || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <TodayIcon
                        sx={{ mr: 0.5, verticalAlign: 'middle' }}
                      />
                      {item.nextDue || 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Leyenda de iconos */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Icon legend:
              </Typography>
              <Stack spacing={0.5}>
                <Typography variant="caption">
                  <CheckCircleIcon
                    fontSize="small"
                    color="success"
                    sx={{ verticalAlign: 'middle', mr: 0.5 }}
                  />
                  Closed / preventive service up to date
                </Typography>
                <Typography variant="caption">
                  <WarningAmberIcon
                    fontSize="small"
                    color="warning"
                    sx={{ verticalAlign: 'middle', mr: 0.5 }}
                  />
                  Open / preventive service due or overdue
                </Typography>
                <Typography variant="caption">
                  <TodayIcon
                    fontSize="small"
                    sx={{ verticalAlign: 'middle', mr: 0.5 }}
                  />
                  Date of last completed service or next recommended date
                </Typography>
              </Stack>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PreventiveServicesReport;
